import { buildQueryString } from '@izimi/utils';

import type {
  ApiRequestBodyType,
  ApiRequestPathParamType,
  ApiRequestQueryParamType,
  ApiResponseType,
} from '..';
import type FetchService from '../FetchService';
import type {
  AcceptRejectSharedDocumentsResponse,
  ApiDocument,
  GetDocumentsQueryParams,
  GetDocumentsResponse,
} from './types';

class DocumentsApi {
  private version = 'v2';

  protected baseUrl = `/${this.version}/documents`;

  constructor(protected fetchService: FetchService) {}

  getDocuments(params: GetDocumentsQueryParams) {
    const qs = buildQueryString(params);
    return this.fetchService.get<GetDocumentsResponse>(`${this.baseUrl}${qs}`);
  }

  getDocument({ documentId }: ApiRequestPathParamType<'getDocument'>) {
    return this.fetchService.get<ApiDocument>(`${this.baseUrl}/${documentId}`);
  }

  moveDocumentsToTrash(documentIds: ApiRequestBodyType<'trashDocuments'>) {
    return this.fetchService.post<ApiDocument[]>(`${this.baseUrl}/trash`, documentIds);
  }

  updateDocument(
    { documentId }: ApiRequestPathParamType<'updateDocument'>,
    values: Partial<ApiDocument>,
  ) {
    return this.fetchService.put<ApiDocument>(`${this.baseUrl}/${documentId}`, values);
  }

  copyDocument({ documentId }: ApiRequestPathParamType<'copySharedDocument'>) {
    return this.fetchService.post<ApiDocument>(`${this.baseUrl}/${documentId}/copy`);
  }

  shareDocuments(values: ApiRequestBodyType<'shareDocuments'>) {
    return this.fetchService.post<ApiDocument[]>(`${this.baseUrl}/share`, values);
  }

  unshareDocuments(values: ApiRequestBodyType<'unshareDocuments'>) {
    return this.fetchService.post<ApiDocument[]>(`${this.baseUrl}/unshare`, values);
  }

  acceptRejectSharedDocuments(values: ApiRequestBodyType<'acceptRejectDocuments'>) {
    return this.fetchService.post<AcceptRejectSharedDocumentsResponse>(
      `${this.baseUrl}/accept-reject`,
      values,
    );
  }

  unTrashDocuments(documentIds: ApiRequestBodyType<'untrashDocuments'>) {
    return this.fetchService.post<ApiDocument[]>(`${this.baseUrl}/untrash`, documentIds);
  }

  /**
   * Delete the documents without going through the trash bin
   */
  permanentlyDeleteDocuments(
    documentIds: ApiRequestBodyType<'deleteDocuments'>,
    params?: ApiRequestQueryParamType<'deleteDocuments'>,
  ) {
    const qs = buildQueryString(params);
    return this.fetchService.post<void>(`${this.baseUrl}/delete${qs}`, documentIds);
  }

  getLabels() {
    return this.fetchService.get<ApiResponseType<'getLabels'>>('/tags');
  }

  markDocumentsSeen(documentIds: ApiRequestBodyType<'markDocumentsAsSeen'>, seen: boolean) {
    return this.fetchService.post<Array<ApiDocument>>(
      `${this.baseUrl}/${seen ? 'seen' : 'unseen'}`,
      documentIds,
    );
  }

  documentExtendTrashDate(documentIds: Array<ApiDocument['id']>) {
    return this.fetchService.post<Array<ApiDocument>>(
      `${this.baseUrl}/auto-deletion-restart`,
      documentIds,
    );
  }
}

export default DocumentsApi;
